import { Skeleton } from "components/characters/Skeleton";
import { ContentBlockDisplay } from "components/landing-pages/ContentBlockDisplay";
import { FaqBlockDisplay } from "components/landing-pages/FaqBlockDisplay";
import { HallOfFameBlockDisplay } from "components/landing-pages/HallOfFameBlockDisplay";
import { ImageTextBlockDisplay } from "components/landing-pages/ImageTextBlockDisplay";
import { TestimonialBlockDisplay } from "components/landing-pages/TestimonialBlockDisplay";
import { ThreeCardBlockDisplay } from "components/landing-pages/ThreeCardBlockDisplay";
import { VideoBlockDisplay } from "components/landing-pages/VideoBlockDisplay";
import { TextPlaceholder } from "components/placeholders/TextPlaceholder";
import { GlobalContext } from "contexts/GlobalContext";
import { Page } from "pages/Page";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GetLandingPageResult } from "results/content/GetLandingPageResult";
import { RouteList } from "Routes";

const LandingPage = () => {
    const { pathname } = window.location;

    const { contentService, catchServerError } = useContext(GlobalContext);
    const { getLandingPage } = contentService;

    const [data, setData] = useState<GetLandingPageResult>();
    const [page404, setPage404] = useState(false);
    const [title, setTitle] = useState("...");
    const [description, setDescription] = useState("");
    const [noIndex, setNoIndex] = useState(false);

    // fetch the landing page content
    useEffect(() => {
        let isMounted = true;

        getLandingPage(pathname)
            .then(x => {
                if (isMounted) {
                    setData(x);
                    setTitle(x.name);
                    setNoIndex(!x.isPublic);
                    setDescription(x.description);
                }
            })
            .catch(() => {
                setPage404(true);
                setTitle("404");
                setNoIndex(true);
            });

        return () => {
            isMounted = false;
        };
    }, [catchServerError, getLandingPage, pathname]);

    return (
        <Page
            canonical={pathname}
            description={description}
            title={title}
            useContainer={false}
            noIndex={noIndex}
            hideBottomPadding={true}
        >
            {page404 ? (
                <div className="container pb-5">
                    <div className="vstack gap-4 text-center">
                        <div className="pb-3">
                            <Skeleton />
                        </div>

                        <h1 className="h0">404 - Page Not Found</h1>

                        <div className="lead">The page you are looking for could not be found at this URL.</div>

                        <div>
                            If you think there's an error, please{" "}
                            <Link to={RouteList.Contact} className="link-white">
                                contact us
                            </Link>
                            .
                        </div>
                    </div>
                </div>
            ) : !data ? (
                <div className="container pb-5">
                    <div className="vstack gap-4 gap-lg-5 flex-lg-row justify-content-center align-items-center">
                        <div className="text-center text-lg-start vstack gap-3">
                            <h1 className="h1">
                                <TextPlaceholder />
                            </h1>
                            <div className="lead">
                                <TextPlaceholder />
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="vstack">
                    <div className="container pb-5">
                        <div className="vstack gap-4 gap-lg-5 flex-lg-row justify-content-center align-items-center">
                            <div className="vstack gap-3">
                                <h1 className="h0">{data.name}</h1>
                                <div className="lead">{data.description}</div>
                            </div>
                        </div>
                    </div>

                    {data.blocks.map(block => {
                        const { type, backgroundColor, sortOrder } = block;

                        return (
                            <div key={sortOrder} className={`text-bg-${backgroundColor}`}>
                                <div className="container py-5">
                                    {type === "Content" && <ContentBlockDisplay block={block} />}
                                    {type === "Video" && <VideoBlockDisplay block={block} />}
                                    {type === "ImageText" && <ImageTextBlockDisplay block={block} />}
                                    {type === "Testimonial" && <TestimonialBlockDisplay block={block} />}
                                    {type === "Faq" && <FaqBlockDisplay block={block} />}
                                    {type === "ThreeCard" && <ThreeCardBlockDisplay block={block} />}
                                    {type === "HallOfFame" && <HallOfFameBlockDisplay block={block} />}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Page>
    );
};

export default LandingPage;
